import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { followupsApi } from '../api'
import { Followup, STAGE_COLORS, PRIORITY_COLORS, STAGE_LABELS } from '../types'

export default function Followups() {
  const [followups, setFollowups] = useState<Followup[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0])
  const [viewMode, setViewMode] = useState<'today' | 'all'>('today')

  useEffect(() => {
    loadFollowups()
  }, [selectedDate, viewMode])

  async function loadFollowups() {
    try {
      setLoading(true)
      if (viewMode === 'today') {
        const data = await followupsApi.today()
        setFollowups(data)
      } else {
        const data = await followupsApi.list({ date: selectedDate })
        setFollowups(data)
      }
    } catch (error) {
      console.error('Failed to load followups:', error)
    } finally {
      setLoading(false)
    }
  }

  async function handleComplete(id: number) {
    try {
      await followupsApi.complete(id)
      await loadFollowups()
    } catch (error) {
      console.error('Failed to complete followup:', error)
    }
  }

  async function handleReschedule(id: number) {
    const newDate = prompt('Enter new date and time (YYYY-MM-DD HH:MM):')
    if (!newDate) return
    
    try {
      await followupsApi.reschedule(id, newDate)
      await loadFollowups()
    } catch (error) {
      console.error('Failed to reschedule followup:', error)
    }
  }

  return (
    <div className="pb-20 lg:pb-0">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 lg:mb-6">
        <h1 className="text-xl lg:text-2xl font-bold">Follow-ups</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('today')}
            className={`px-3 lg:px-4 py-2 rounded-lg font-medium text-sm lg:text-base transition-colors ${
              viewMode === 'today'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Today
          </button>
          <button
            onClick={() => setViewMode('all')}
            className={`px-3 lg:px-4 py-2 rounded-lg font-medium text-sm lg:text-base transition-colors ${
              viewMode === 'all'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            All Dates
          </button>
        </div>
      </div>

      {viewMode === 'all' && (
        <div className="card mb-4 lg:mb-6">
          <div className="flex items-center gap-4">
            <label className="label mb-0 whitespace-nowrap">Select Date:</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="input-field flex-1"
            />
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">Loading follow-ups...</div>
      ) : followups.length === 0 ? (
        <div className="card text-center py-8">
          <div className="text-4xl mb-4">📞</div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No follow-ups</h3>
          <p className="text-gray-500 text-sm">
            {viewMode === 'today' 
              ? "You don't have any follow-ups scheduled for today."
              : `No follow-ups found for ${new Date(selectedDate).toLocaleDateString()}.`
            }
          </p>
        </div>
      ) : (
        <div className="space-y-3 lg:space-y-4">
          {followups.map((followup) => (
            <div key={followup.id} className="card">
              <div className="flex items-start gap-3 lg:gap-4">
                <div className="w-10 h-10 lg:w-12 lg:h-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl lg:text-2xl">
                    {followup.type === 'call' ? '📞' : 
                     followup.type === 'whatsapp' ? '💬' : 
                     followup.type === 'email' ? '📧' : 
                     followup.type === 'meeting' ? '🤝' : '🎯'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <Link 
                      to={`/leads/${followup.lead_id}`}
                      className="font-semibold text-base lg:text-lg hover:text-primary-600 truncate"
                    >
                      {(followup as any).coaching_name}
                    </Link>
                    <div className="flex items-center gap-1.5">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STAGE_COLORS[(followup as any).stage]}`}>
                        {STAGE_LABELS[(followup as any).stage as keyof typeof STAGE_LABELS]}
                      </span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${PRIORITY_COLORS[(followup as any).priority]}`}>
                        {(followup as any).priority}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-2 lg:gap-4 mt-1.5 text-xs lg:text-sm text-gray-600">
                    <a href={`tel:${(followup as any).phone}`} className="hover:text-primary-600">
                      📞 {(followup as any).phone}
                    </a>
                    {(followup as any).whatsapp && (
                      <a href={`https://wa.me/${(followup as any).whatsapp}`} target="_blank" rel="noopener noreferrer" className="hover:text-primary-600">
                        💬 {(followup as any).whatsapp}
                      </a>
                    )}
                  </div>
                  
                  {followup.notes && (
                    <p className="mt-2 text-gray-600 text-xs lg:text-sm">{followup.notes}</p>
                  )}
                  
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mt-3">
                    <div>
                      <div className="text-xs lg:text-sm text-gray-500">
                        {new Date(followup.scheduled_date).toLocaleString()}
                      </div>
                      <div className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full mt-1 ${
                        followup.status === 'completed' ? 'bg-green-100 text-green-700' :
                        followup.status === 'missed' ? 'bg-red-100 text-red-700' :
                        followup.status === 'rescheduled' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {followup.status}
                      </div>
                    </div>
                    
                    {followup.status === 'pending' && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleComplete(followup.id)}
                          className="btn-primary text-xs lg:text-sm py-1.5 px-3"
                        >
                          ✓ Complete
                        </button>
                        <button
                          onClick={() => handleReschedule(followup.id)}
                          className="btn-secondary text-xs lg:text-sm py-1.5 px-3"
                        >
                          🔄 Reschedule
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
